import NumberFormat from 'react-number-format';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { useSnackbar } from 'notistack';
import { Button, Dialog, DialogActions, DialogContent, DialogProps, DialogTitle, Grid, TextField } from '@mui/material';

import { NomenclatureGroupFine } from '@/types';
import { useCreateNomenclatureGroupFineMutation } from '@/services';
import { messages } from '@/utils/helpers';
import { SearchPoints } from '@/pages/Points/components';

export interface CreateNomenclatureGroupFineProps extends DialogProps {
  onClose(): void;
}

const CreateNomenclatureGroupFine: React.FC<CreateNomenclatureGroupFineProps> = ({ onClose, ...props }) => {
  const { enqueueSnackbar } = useSnackbar();

  const [createNomenclatureGroupFine, createNomenclatureGroupFineResponse] = useCreateNomenclatureGroupFineMutation();

  const { values, errors, touched, setFieldValue, handleSubmit, handleChange } = useFormik<NomenclatureGroupFine>({
    initialValues: {
      gt_30_days: 0,
      lt_30_days: 0,
      point: null,
    },
    validationSchema: yup.object().shape({
      gt_30_days: yup.number().required(messages.form.REQUIRED),
      lt_30_days: yup.number().required(messages.form.REQUIRED),
      point: yup.object().nullable().required(messages.form.SELECT_FROM_LIST),
    }),
    async onSubmit(nomenclatureGroupFine, { resetForm }) {
      try {
        await createNomenclatureGroupFine(nomenclatureGroupFine).unwrap();

        onClose();
        resetForm();
      } catch (error) {
        enqueueSnackbar(error.data, { variant: 'error' });
      }
    },
  });

  return (
    <Dialog fullWidth {...props}>
      <DialogTitle fontWeight="bold">Добавить штраф</DialogTitle>

      <form onSubmit={handleSubmit}>
        <DialogContent dividers>
          <Grid container spacing={3}>
            <Grid item lg={6} md={6} xs={12}>
              <NumberFormat
                customInput={TextField}
                name="gt_30_days"
                type="tel"
                label="Общая пени (>= 30)"
                placeholder="Введите значение"
                value={values.gt_30_days}
                onChange={handleChange}
                disabled={createNomenclatureGroupFineResponse.isLoading}
                error={touched.gt_30_days && Boolean(errors.gt_30_days)}
                helperText={touched.gt_30_days ? errors.gt_30_days : ''}
                fullWidth
              />
            </Grid>

            <Grid item lg={6} md={6} xs={12}>
              <NumberFormat
                customInput={TextField}
                name="lt_30_days"
                type="tel"
                label="Общая пени (<= 30)"
                placeholder="Введите значение"
                value={values.lt_30_days}
                onChange={handleChange}
                disabled={createNomenclatureGroupFineResponse.isLoading}
                error={touched.lt_30_days && Boolean(errors.lt_30_days)}
                helperText={touched.lt_30_days ? errors.lt_30_days : ''}
                fullWidth
              />
            </Grid>

            <Grid item lg={6} md={6} xs={12}>
              <SearchPoints
                value={values.point}
                onChange={(event, option) => setFieldValue('point', option)}
                disabled={createNomenclatureGroupFineResponse.isLoading}
                getOptionLabel={(option) => option.name}
                renderInput={(props) => (
                  <TextField
                    {...props}
                    name="point"
                    label="Точка"
                    placeholder="Выберите из списка"
                    error={Boolean(touched.point && errors.point)}
                    helperText={touched.point && errors.point}
                  />
                )}
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions>
          <Button
            type="button"
            color="secondary"
            variant="outlined"
            disabled={createNomenclatureGroupFineResponse.isLoading}
            onClick={onClose}
          >
            Закрыть
          </Button>

          <Button type="submit" variant="contained" disabled={createNomenclatureGroupFineResponse.isLoading}>
            Добавить
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default CreateNomenclatureGroupFine;
